import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { User, CreateUserRequest, UpdateUserRequest, UserRole } from '../types';
import authService from '../services/authService';
import userService from '../services/userService';

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    role: UserRole.USER as UserRole,
    active: true
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const userList = await authService.getAllUsers();
      setUsers(userList);
    } catch (error) {
      toast.error('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await authService.updateUser(editingUser.id, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          username: formData.username,
          role: formData.role,
          active: formData.active,
          ...(formData.password && { password: formData.password })
        });
        toast.success('Benutzer aktualisiert');
        setEditingUser(null);
      } else {
        await authService.createUser({
          firstName: formData.firstName,
          lastName: formData.lastName,
          username: formData.username,
          password: formData.password,
          role: formData.role
        });
        toast.success('Benutzer erstellt');
        setShowCreateForm(false);
      }
      resetForm();
      await loadUsers();
    } catch (error: any) {
      toast.error(error.message || 'Fehler beim Speichern');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({ firstName: user.firstName, lastName: user.lastName, username: user.username, password: '', role: user.role, active: user.active });
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData({ firstName: '', lastName: '', username: '', password: '', role: UserRole.USER, active: true });
    setEditingUser(null);
    setShowCreateForm(false);
  };

  const handleDelete = async (user: User) => {
    if (!window.confirm(`Benutzer "${user.firstName} ${user.lastName}" (${user.username}) wirklich löschen?`)) {
      return;
    }
    try {
      await userService.delete(user.id);
      toast.success('Benutzer gelöscht');
      await loadUsers();
    } catch (error: any) {
      toast.error(error.message || 'Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#216dab]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight transition-colors">Benutzerverwaltung</h1>
          <p className="text-slate-400 dark:text-slate-500 text-base font-normal transition-colors">Systemzugriffe steuern und Rollen zuweisen</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-6 py-3 bg-[#216dab] dark:bg-[#216dab] hover:bg-[#1a5a8f] dark:hover:bg-[#216dab] text-white rounded-2xl font-bold text-sm shadow-lg shadow-[#216dab]/25 transition-all flex items-center gap-2 w-fit"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Neuer Benutzer
        </button>
      </div>

      <div className="glass-card overflow-hidden transition-all duration-500">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-black/5 dark:border-white/5 bg-black/2 dark:bg-white/2">
                <th className="px-6 py-4 text-sm font-medium text-slate-400">Name</th>
                <th className="px-6 py-4 text-sm font-medium text-slate-400">Benutzername</th>
                <th className="px-6 py-4 text-sm font-medium text-slate-400">Rolle</th>
                <th className="px-6 py-4 text-sm font-medium text-slate-400">Status</th>
                <th className="px-6 py-4 text-sm font-medium text-slate-400 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5 dark:divide-white/5">
              {users.map((user) => (
                <tr key={user.id} className="group hover:bg-black/2 dark:hover:bg-white/2 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#216dab]/10 dark:bg-[#216dab]/20 border border-[#216dab]/20 flex items-center justify-center font-bold text-[#216dab] dark:text-[#3aa6e9] text-sm shadow-inner transition-colors">
                        {user.firstName[0]}{user.lastName[0]}
                      </div>
                      <span className="text-base font-medium text-slate-800 dark:text-slate-200 transition-colors">
                        {user.firstName} {user.lastName}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400 font-medium transition-colors">
                    {user.username}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2.5 py-1 rounded-lg text-sm font-medium transition-colors ${
                      user.role === UserRole.ADMIN
                        ? 'bg-[#216dab]/10 text-[#216dab] dark:text-[#3aa6e9] border border-[#216dab]/20'
                        : 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border border-black/5 dark:border-white/5'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                       <div className={`w-1.5 h-1.5 rounded-full ${user.active ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
                       <span className="text-sm font-normal text-slate-600 dark:text-slate-400 transition-colors">{user.active ? 'Aktiv' : 'Inaktiv'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleEdit(user)}
                        className="p-2 text-slate-400 hover:text-[#216dab] dark:hover:text-[#3aa6e9] transition-colors"
                        title="Bearbeiten"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        className="p-2 text-slate-400 hover:text-rose-500 transition-colors"
                        title="Löschen"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-950/40 dark:bg-slate-950/60 backdrop-blur-sm transition-all" onClick={resetForm} />
          <div className="glass-card w-full max-w-lg relative z-10 p-8 animate-in zoom-in-95 duration-200">
            <h2 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight mb-8">
              {editingUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-500 px-1">Vorname</label>
                  <input
                    type="text" required value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-500 px-1">Nachname</label>
                  <input
                    type="text" required value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 transition-all"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-500 px-1">Benutzername</label>
                <input
                  type="text" required value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-500 px-1">Passwort {editingUser && '(leer lassen)'}</label>
                <input
                  type="password" required={!editingUser} value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-500 px-1">Rolle</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                  className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 appearance-none transition-all"
                >
                  <option value={UserRole.USER}>User</option>
                  <option value={UserRole.ADMIN}>Administrator</option>
                </select>
              </div>
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={resetForm} className="flex-1 px-6 py-3 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-slate-500 rounded-2xl font-bold transition-all">Abbrechen</button>
                <button type="submit" className="flex-1 px-6 py-3 bg-[#216dab] dark:bg-[#216dab] hover:bg-[#1a5a8f] dark:hover:bg-[#216dab] text-white rounded-2xl font-bold shadow-lg shadow-[#216dab]/25 transition-all">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
